import logoUrl from "../assets/images/logo.png";
// Rapport PDF - réutilise le design TEMPLATE_DEVIS.pdf (Africa Systems)
// Génération 100% front avec pdf-lib, sans dépendance backend
// Using CommonJS require due to pdf-lib v1.17.1 broken ESM entry point
const { PDFDocument, StandardFonts, rgb } = require("pdf-lib");

const ODOO_PRIMARY = rgb(0.443, 0.294, 0.404); // #714B67
const ODOO_MUTED = rgb(0.424, 0.459, 0.49);
const ODOO_BORDER = rgb(0.867, 0.886, 0.902);
const TEXT_DARK = rgb(0.129, 0.145, 0.16);

// Les polices PDF standard utilisent WinAnsi et refusent certains espaces Unicode.
const sanitizePdfText = (value) => String(value ?? "")
  .replace(/[\u202F\u00A0]/g, " ")
  .replace(/[\u2018\u2019]/g, "'")
  .replace(/[\u201C\u201D]/g, '"')
  .replace(/[\u2013\u2014]/g, "-")
  .replace(/\u2026/g, "...");

// FCFA format
const fmtMoney = (v) => sanitizePdfText((Number(v) || 0).toLocaleString("fr-FR") + " FCFA");
const fmtDate = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  if (isNaN(d)) return String(v);
  return sanitizePdfText(d.toLocaleDateString("fr-FR"));
};

function stageNameOf(stages, id) {
  const m = new Map(stages.map((s) => [String(s.id), s.name]));
  return m.get(String(id)) || "";
}
function userNameOf(users, id) {
  const m = new Map(users.map((u) => [String(u.id), u.name || u.username || u.email || ""]));
  return m.get(String(id)) || "";
}

async function tryLoadTemplateBytes() {
  const candidates = ["/templates/TEMPLATE_DEVIS.pdf", "/TEMPLATE_DEVIS.pdf", "templates/TEMPLATE_DEVIS.pdf"];
  for (const url of candidates) {
    try {
      const res = await fetch(url);
      if (!res.ok) continue;
      const ct = res.headers.get("content-type") || "";
      if (ct.includes("text/html")) continue;
      const buf = await res.arrayBuffer();
      if (buf.byteLength > 1000) return buf;
    } catch {}
  }
  return null;
}

async function generateLegacyCrmPdf({
  leads = [],
  stages = [],
  users = [],
  teams = [],
  filters = {},
  search = "",
  title = "Rapport CRM — Pipeline",
}) {
  const tplBytes = await tryLoadTemplateBytes();
  let tplDoc = null;
  if (tplBytes) {
    try { tplDoc = await PDFDocument.load(tplBytes); } catch {}
  }

  const doc = await PDFDocument.create();
  const font = await doc.embedFont(StandardFonts.Helvetica);
  const fontBold = await doc.embedFont(StandardFonts.HelveticaBold);

  const A4_W = 595.28;
  const A4_H = 841.89;
  const MARGIN = 32;

  const totalExpected = leads.reduce((s, l) => s + (Number(l.expected_revenue) || 0), 0);
  const totalProrated = leads.reduce((s, l) => s + ((Number(l.expected_revenue) || 0) * (Number(l.probability) || 0) / 100), 0);
  const avgProba = leads.length ? leads.reduce((s, l) => s + (Number(l.probability) || 0), 0) / leads.length : 0;

  const byStage = new Map();
  leads.forEach((l) => {
    const n = stageNameOf(stages, l.stage_id) || "Sans étape";
    const cur = byStage.get(n) || { count: 0, expected: 0 };
    cur.count += 1;
    cur.expected += Number(l.expected_revenue) || 0;
    byStage.set(n, cur);
  });

  let isFirstPage = true;
  let templatePageEmbedded = false;

  const addPage = async () => {
    if (isFirstPage && tplDoc) {
      try {
        const [copied] = await doc.copyPages(tplDoc, [0]);
        const p = doc.addPage([copied.getWidth(), copied.getHeight()]);
        const { width, height } = copied.getSize();
        isFirstPage = false;
        templatePageEmbedded = true;
        return p;
      } catch {
        isFirstPage = false;
        return doc.addPage([A4_W, A4_H]);
      }
    }
    isFirstPage = false;
    return doc.addPage([A4_W, A4_H]);
  };

  let page = doc.addPage([A4_W, A4_H]);
  isFirstPage = false;

  const drawRect = (pg, x, y, w, h, color) => pg.drawRectangle({ x, y, width: w, height: h, color });
  const drawText = (pg, text, x, y, opts = {}) => {
    pg.drawText(sanitizePdfText(text), {
      x, y,
      size: opts.size || 8,
      font: opts.bold ? fontBold : font,
      color: opts.color || TEXT_DARK,
      maxWidth: opts.maxWidth,
      lineHeight: opts.lineHeight || 10,
    });
  };

  const drawHeader = (pg, pageNum, totalPages) => {
    drawRect(pg, 0, A4_H - 48, A4_W, 48, ODOO_PRIMARY);
    drawText(pg, "AFRICA SYSTEMS  |  DBA  |  LEWOO SARL", MARGIN, A4_H - 28, { size: 9, bold: true, color: rgb(1, 1, 1) });
    drawText(pg, "Power of innovation — Bonapriso Douala, Cameroun", MARGIN, A4_H - 40, { size: 7, color: rgb(1, 1, 1) });
    drawText(pg, `Page ${pageNum} / ${totalPages}`, A4_W - MARGIN - 70, A4_H - 28, { size: 7, color: rgb(1, 1, 1) });
  };

  const drawFooter = (pg) => {
    const y = 28;
    drawRect(pg, 0, 0, A4_W, 56, rgb(0.97, 0.97, 0.97));
    drawText(pg, "OM: 657 05 15 80", MARGIN, 42, { size: 6, color: ODOO_MUTED });
    drawText(pg, "Mobile Payment OM : 657 05 15 80  —  DICKSON ZEMELE TAKOUZIM", MARGIN, 32, { size: 6, color: ODOO_MUTED });
    drawText(pg, "+237 6 93 83 02 06  |  info@africasystems.com  |  https://www.africasystems.com  |  M102015145032Z", MARGIN, 22, { size: 6, color: ODOO_MUTED });
    drawText(pg, "THANK YOU FOR TRUSTING US …", MARGIN, 12, { size: 6, bold: true, color: ODOO_PRIMARY });
    pg.drawLine({ start: { x: MARGIN, y: 50 }, end: { x: A4_W - MARGIN, y: 50 }, thickness: 0.5, color: ODOO_BORDER });
  };

  const pages = [page];
  let y = A4_H - 72;

  drawText(page, title.toUpperCase(), MARGIN, y, { size: 14, bold: true, color: ODOO_PRIMARY });
  y -= 14;
  drawText(page, `Généré le ${new Date().toLocaleString("fr-FR")}  •  ${leads.length} opportunité(s)  •  Vue: ${filters._view || "Pipeline"}`, MARGIN, y, { size: 7, color: ODOO_MUTED });
  y -= 10;

  const filterLines = [];
  if (search) filterLines.push(`Recherche: "${search}"`);
  if (filters.type && filters.type !== "all") filterLines.push(`Type: ${filters.type}`);
  if (filters.stage_id && filters.stage_id !== "all") filterLines.push(`Étape: ${stageNameOf(stages, filters.stage_id)}`);
  if (filters.team_id && filters.team_id !== "all") filterLines.push(`Équipe: ${teams.find(t => String(t.id)===String(filters.team_id))?.name || filters.team_id}`);
  if (filters.user_id && filters.user_id !== "all") filterLines.push(`Commercial: ${userNameOf(users, filters.user_id)}`);
  const filterText = filterLines.length ? filterLines.join("  |  ") : "Aucun filtre (tous les enregistrements)";

  drawRect(page, MARGIN, y - 18, A4_W - MARGIN * 2, 18, rgb(0.973, 0.933, 0.949));
  page.drawRectangle({ x: MARGIN, y: y - 18, width: A4_W - MARGIN * 2, height: 18, borderColor: ODOO_BORDER, borderWidth: 0.5 });
  drawText(page, filterText, MARGIN + 6, y - 12, { size: 6.5, color: TEXT_DARK, maxWidth: A4_W - MARGIN * 2 - 12 });
  y -= 28;

  const kpiCards = [
    { label: "Opportunités", value: String(leads.length) },
    { label: "CA attendu", value: fmtMoney(totalExpected) },
    { label: "CA proratisé", value: fmtMoney(totalProrated) },
    { label: "Proba moy.", value: `${avgProba.toFixed(1)} %` },
  ];
  const cardW = (A4_W - MARGIN * 2 - 18) / 4;
  const cardH = 38;
  let cx = MARGIN;
  kpiCards.forEach((k) => {
    page.drawRectangle({ x: cx, y: y - cardH, width: cardW, height: cardH, color: rgb(1, 1, 1), borderColor: ODOO_BORDER, borderWidth: 0.5 });
    drawRect(page, cx, y - 2, cardW, 2, ODOO_PRIMARY);
    drawText(page, k.label.toUpperCase(), cx + 6, y - 14, { size: 6, color: ODOO_MUTED, bold: true });
    drawText(page, k.value, cx + 6, y - 28, { size: 9, bold: true, color: TEXT_DARK });
    cx += cardW + 6;
  });
  y -= cardH + 14;

  const cols = [
    { key: "name", header: "Opportunité", w: 120 },
    { key: "stage", header: "Étape", w: 78 },
    { key: "sales", header: "Commercial", w: 78 },
    { key: "expected", header: "CA attendu", w: 70, align: "right" },
    { key: "proba", header: "Proba", w: 38, align: "center" },
    { key: "deadline", header: "Échéance", w: 60 },
  ];
  const tableW = cols.reduce((s, c) => s + c.w, 0);
  const tableX = MARGIN + (A4_W - MARGIN * 2 - tableW) / 2;
  const rowH = 16;
  const headerH = 18;

  const ensureSpace = async (needed) => {
    if (y - needed < 60) {
      page = await addPage();
      pages.push(page);
      y = A4_H - 62;
    }
  };

  await ensureSpace(headerH);
  drawRect(page, tableX, y - headerH, tableW, headerH, ODOO_PRIMARY);
  let tx = tableX;
  cols.forEach((c) => {
    if (c !== cols[0]) page.drawLine({ start: { x: tx, y: y - headerH }, end: { x: tx, y: y }, thickness: 0.5, color: rgb(1, 1, 1) });
    const align = c.align || "left";
    let textX = tx + 4;
    if (align === "center") textX = tx + c.w / 2 - (c.header.length * 2.2);
    if (align === "right") textX = tx + c.w - 6 - (c.header.length * 2.2);
    drawText(page, c.header, textX, y - 12, { size: 6, bold: true, color: rgb(1, 1, 1) });
    tx += c.w;
  });
  y -= headerH;

  const maxRowsPerPageApprox = 28;
  let rowIdx = 0;
  for (const l of leads) {
    await ensureSpace(rowH);
    const isAlt = rowIdx % 2 === 1;
    if (isAlt) drawRect(page, tableX, y - rowH, tableW, rowH, rgb(0.973, 0.973, 0.973));
    page.drawRectangle({ x: tableX, y: y - rowH, width: tableW, height: rowH, borderColor: ODOO_BORDER, borderWidth: 0.4 });

    const stage = stageNameOf(stages, l.stage_id) || "—";
    const sales = userNameOf(users, l.user_id) || l.user_name || "—";
    const expected = fmtMoney(l.expected_revenue);
    const proba = `${Number(l.probability) || 0}%`;
    const deadline = l.date_deadline ? fmtDate(l.date_deadline) : "—";

    const rowData = [
      { v: l.name || "—", w: cols[0].w },
      { v: stage, w: cols[1].w },
      { v: sales, w: cols[2].w },
      { v: expected, w: cols[3].w, align: "right" },
      { v: proba, w: cols[4].w, align: "center" },
      { v: deadline, w: cols[5].w },
    ];
    let rx = tableX;
    rowData.forEach((cell, ci) => {
      let txt = String(cell.v);
      const maxChars = Math.floor(cell.w / 5.2);
      if (txt.length > maxChars) txt = txt.slice(0, maxChars - 1) + "…";
      let x = rx + 4;
      if (cell.align === "center") x = rx + cell.w / 2 - (txt.length * 1.9);
      if (cell.align === "right") x = rx + cell.w - 4 - (txt.length * 3.0);
      const isMoney = ci === 3;
      drawText(page, txt, x, y - 11, { size: 6.5, color: isMoney ? TEXT_DARK : ODOO_MUTED, bold: isMoney });
      if (ci < rowData.length - 1) {
        page.drawLine({ start: { x: rx + cell.w, y: y - rowH }, end: { x: rx + cell.w, y: y }, thickness: 0.3, color: ODOO_BORDER });
      }
      rx += cell.w;
    });

    y -= rowH;
    rowIdx++;
    if (rowIdx % maxRowsPerPageApprox === 0 && rowIdx < leads.length) {
    }
  }

  if (leads.length === 0) {
    await ensureSpace(24);
    drawText(page, "Aucun enregistrement pour les filtres sélectionnés.", tableX + 6, y - 14, { size: 8, color: ODOO_MUTED });
    y -= 24;
  }

  await ensureSpace(rowH);
  drawRect(page, tableX, y - rowH, tableW, rowH, ODOO_PRIMARY);
  let rx2 = tableX;
  drawText(page, `TOTAL  (${leads.length})`, rx2 + 4, y - 11, { size: 7, bold: true, color: rgb(1, 1, 1) });
  rx2 += cols[0].w + cols[1].w + cols[2].w;
  const totalStr = fmtMoney(totalExpected);
  drawText(page, totalStr, rx2 + cols[3].w - 4 - totalStr.length * 3.0, y - 11, { size: 7, bold: true, color: rgb(1, 1, 1) });
  y -= rowH + 12;

  await ensureSpace(40);
  drawText(page, "RÉPARTITION PAR ÉTAPE", MARGIN, y, { size: 8, bold: true, color: ODOO_PRIMARY });
  y -= 4;
  page.drawLine({ start: { x: MARGIN, y }, end: { x: A4_W - MARGIN, y }, thickness: 0.8, color: ODOO_PRIMARY });
  y -= 12;

  const sCols = [
    { header: "Étape", w: 220 },
    { header: "Nombre", w: 70, align: "center" },
    { header: "CA attendu", w: 120, align: "right" },
  ];
  const sTableW = sCols.reduce((s, c) => s + c.w, 0);
  const sTableX = MARGIN;

  await ensureSpace(headerH);
  drawRect(page, sTableX, y - headerH, sTableW, headerH, rgb(0.95, 0.95, 0.95));
  page.drawRectangle({ x: sTableX, y: y - headerH, width: sTableW, height: headerH, borderColor: ODOO_BORDER, borderWidth: 0.5 });
  let sx = sTableX;
  sCols.forEach((c) => {
    let x = sx + 6;
    if (c.align === "center") x = sx + c.w / 2 - c.header.length * 2;
    if (c.align === "right") x = sx + c.w - 6 - c.header.length * 2.2;
    drawText(page, c.header, x, y - 12, { size: 6, bold: true, color: ODOO_MUTED });
    sx += c.w;
  });
  y -= headerH;

  const orderedNames = stages.filter(s => s.is_active !== false).map(s => s.name);
  const stageKeys = [...new Set([...orderedNames, ...byStage.keys()])].filter(k => byStage.has(k));
  for (const name of stageKeys) {
    await ensureSpace(rowH);
    const cur = byStage.get(name);
    const isAlt = stageKeys.indexOf(name) % 2 === 1;
    if (isAlt) drawRect(page, sTableX, y - rowH, sTableW, rowH, rgb(0.98, 0.98, 0.98));
    page.drawRectangle({ x: sTableX, y: y - rowH, width: sTableW, height: rowH, borderColor: ODOO_BORDER, borderWidth: 0.3 });
    drawText(page, name.length > 38 ? name.slice(0, 37) + "…" : name, sTableX + 6, y - 11, { size: 7, color: TEXT_DARK });
    drawText(page, String(cur.count), sTableX + 220 + 35 - String(cur.count).length * 2, y - 11, { size: 7, color: TEXT_DARK });
    const expStr = fmtMoney(cur.expected);
    drawText(page, expStr, sTableX + 220 + 70 + 120 - 6 - expStr.length * 2.8, y - 11, { size: 7, color: TEXT_DARK });
    y -= rowH;
  }

  const totalPages = pages.length;
  pages.forEach((pg, idx) => {
    if (templatePageEmbedded && idx === 0) {
      pg.drawRectangle({ x: MARGIN, y: 80, width: A4_W - MARGIN * 2, height: A4_H - 160, color: rgb(1, 1, 1) });
    }
    drawHeader(pg, idx + 1, totalPages);
    drawFooter(pg);
  });

  const pdfBytes = await doc.save();
  return new Blob([pdfBytes], { type: "application/pdf" });
}

void generateLegacyCrmPdf;

const REPORT_GREEN = rgb(0.20, 0.45, 0.12);
const REPORT_DARK = rgb(0.27, 0.25, 0.24);
const REPORT_LIGHT_GREEN = rgb(0.92, 0.96, 0.89);

const pdfTextWidth = (font, value, size) => font.widthOfTextAtSize(sanitizePdfText(value), size);

const fetchReportLogo = async () => {
  try {
    const response = await fetch(logoUrl);
    if (!response.ok) return null;
    return response.arrayBuffer();
  } catch {
    return null;
  }
};

function generateWrappedLines(font, value, maxWidth, size, maxLines = 2) {
  const words = sanitizePdfText(value || "-").trim().split(/\s+/);
  const lines = [];
  let current = "";
  words.forEach((word) => {
    const candidate = current ? `${current} ${word}` : word;
    if (pdfTextWidth(font, candidate, size) <= maxWidth || !current) current = candidate;
    else {
      lines.push(current);
      current = word;
    }
  });
  if (current) lines.push(current);
  if (lines.length <= maxLines) return lines;
  const clipped = lines.slice(0, maxLines);
  let last = clipped[maxLines - 1];
  while (pdfTextWidth(font, `${last}...`, size) > maxWidth && last.length > 1) last = last.slice(0, -1);
  clipped[maxLines - 1] = `${last}...`;
  return clipped;
}

async function generateCrmPdf({
  leads = [],
  stages = [],
  users = [],
  teams = [],
  filters = {},
  search = "",
  title = "Rapport CRM - Pipeline",
}) {
  const doc = await PDFDocument.create();
  const font = await doc.embedFont(StandardFonts.Helvetica);
  const fontBold = await doc.embedFont(StandardFonts.HelveticaBold);
  const logoBytes = await fetchReportLogo();
  const logo = logoBytes ? await doc.embedPng(logoBytes) : null;
  const pageW = 595.28;
  const pageH = 841.89;
  const margin = 34;
  const headerH = 94;
  const footerH = 58;
  const contentTop = pageH - headerH - 22;
  const contentBottom = footerH + 18;
  const pages = [];

  const money = (value) => sanitizePdfText(`${(Number(value) || 0).toLocaleString("fr-FR")} FCFA`);
  const expectedTotal = leads.reduce((sum, lead) => sum + (Number(lead.expected_revenue) || 0), 0);
  const weightedTotal = leads.reduce((sum, lead) => sum + ((Number(lead.expected_revenue) || 0) * (Number(lead.probability) || 0)) / 100, 0);
  const stageLabel = (id) => stageNameOf(stages, id) || "Sans étape";
  const salesLabel = (lead) => userNameOf(users, lead.user_id) || lead.user_name || "-";
  const dateLabel = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString("fr-FR");
  };

  const text = (page, value, x, y, options = {}) => page.drawText(sanitizePdfText(value), {
    x,
    y,
    size: options.size || 8,
    font: options.bold ? fontBold : font,
    color: options.color || TEXT_DARK,
    maxWidth: options.maxWidth,
    lineHeight: options.lineHeight || 10,
  });

  const addPage = () => {
    const page = doc.addPage([pageW, pageH]);
    pages.push(page);
    return page;
  };

  const drawHeader = (page, pageNumber) => {
    page.drawRectangle({ x: 0, y: pageH - headerH, width: pageW, height: headerH, color: rgb(1, 1, 1) });
    page.drawRectangle({ x: 114, y: pageH - 78, width: 286, height: 60, color: REPORT_DARK });
    page.drawRectangle({ x: 378, y: pageH - 78, width: pageW - 378, height: 60, color: REPORT_GREEN });
    page.drawSvgPath(`M 350 ${pageH - 78} L 378 ${pageH - 18} L 408 ${pageH - 18} L 380 ${pageH - 78} Z`, { color: rgb(1, 1, 1) });
    if (logo) page.drawImage(logo, { x: 43, y: pageH - 78, width: 54, height: 54 });
    text(page, "AFRICA SYSTEMS  |  DBA  |  LEWOO SARL", 124, pageH - 43, { size: 10, bold: true, color: rgb(1, 1, 1) });
    text(page, "Power of innovation", 124, pageH - 62, { size: 9, color: rgb(1, 1, 1) });
    text(page, "Bonapriso Douala, Cameroun", 398, pageH - 42, { size: 7, bold: true, color: rgb(1, 1, 1), maxWidth: 178 });
    text(page, "+237 6 93 83 02 06", 424, pageH - 57, { size: 7, color: rgb(1, 1, 1) });
    text(page, "info@africasystems.com", 415, pageH - 71, { size: 7, color: rgb(1, 1, 1) });
    text(page, `Page ${pageNumber}`, pageW - margin - 32, pageH - 12, { size: 6, color: ODOO_MUTED });
  };

  const drawFooter = (page) => {
    page.drawRectangle({ x: 0, y: 28, width: pageW, height: 30, color: REPORT_GREEN });
    page.drawRectangle({ x: 0, y: 0, width: pageW, height: 28, color: REPORT_DARK });
    text(page, "THANK YOU FOR TRUSTING US ...", margin, 39, { size: 8, bold: true, color: rgb(1, 1, 1) });
    text(page, "OM: 657 05 15 80", margin, 16, { size: 5.8, color: rgb(1, 1, 1), maxWidth: pageW - margin * 2 });
    text(page, "+237 6 93 83 02 06 | info@africasystems.com | www.africasystems.com", pageW - margin - 245, 6, { size: 5.5, color: rgb(1, 1, 1), maxWidth: 245 });
  };

  let page = addPage();
  let y = contentTop;
  const newContentPage = () => {
    page = addPage();
    y = contentTop;
  };
  const ensureSpace = (height) => {
    if (y - height < contentBottom) newContentPage();
  };

  text(page, title.toUpperCase(), margin, y, { size: 15, bold: true, color: REPORT_DARK });
  y -= 16;
  text(page, `Généré le ${new Date().toLocaleString("fr-FR")} | ${leads.length} opportunité(s) | Vue: ${filters._view || "Pipeline"}`, margin, y, { size: 7, color: ODOO_MUTED });
  y -= 14;

  const filterLines = [];
  if (search) filterLines.push(`Recherche: "${search}"`);
  if (filters.type && filters.type !== "all") filterLines.push(`Type: ${filters.type}`);
  if (filters.stage_id && filters.stage_id !== "all") filterLines.push(`Étape: ${stageLabel(filters.stage_id)}`);
  if (filters.team_id && filters.team_id !== "all") filterLines.push(`Équipe: ${teams.find((team) => String(team.id) === String(filters.team_id))?.name || filters.team_id}`);
  if (filters.user_id && filters.user_id !== "all") filterLines.push(`Commercial: ${userNameOf(users, filters.user_id)}`);
  const filterText = filterLines.length ? filterLines.join(" | ") : "Aucun filtre - tous les enregistrements";
  page.drawRectangle({ x: margin, y: y - 22, width: pageW - margin * 2, height: 22, color: REPORT_LIGHT_GREEN, borderColor: REPORT_GREEN, borderWidth: 0.5 });
  text(page, filterText, margin + 7, y - 14, { size: 6.5, maxWidth: pageW - margin * 2 - 14 });
  y -= 36;

  const summary = [
    ["OPPORTUNITÉS", String(leads.length)],
    ["CA ATTENDU", money(expectedTotal)],
    ["CA PRORATISÉ", money(weightedTotal)],
    ["PROBABILITÉ MOY.", `${leads.length ? (leads.reduce((sum, lead) => sum + (Number(lead.probability) || 0), 0) / leads.length).toFixed(1) : "0.0"} %`],
  ];
  const cardGap = 6;
  const cardW = (pageW - margin * 2 - cardGap * 3) / 4;
  summary.forEach(([label, value], index) => {
    const x = margin + index * (cardW + cardGap);
    page.drawRectangle({ x, y: y - 38, width: cardW, height: 38, color: rgb(1, 1, 1), borderColor: ODOO_BORDER, borderWidth: 0.5 });
    page.drawRectangle({ x, y: y - 3, width: cardW, height: 3, color: REPORT_GREEN });
    text(page, label, x + 6, y - 14, { size: 5.5, bold: true, color: ODOO_MUTED });
    text(page, value, x + 6, y - 29, { size: 7.5, bold: true, color: REPORT_DARK, maxWidth: cardW - 12 });
  });
  y -= 54;

  const columns = [
    { key: "name", label: "Opportunité", width: 145 },
    { key: "stage", label: "Étape", width: 86 },
    { key: "sales", label: "Commercial", width: 86 },
    { key: "expected", label: "CA attendu", width: 82, align: "right" },
    { key: "probability", label: "Proba", width: 42, align: "center" },
    { key: "deadline", label: "Échéance", width: 86 },
  ];
  const tableW = columns.reduce((sum, column) => sum + column.width, 0);
  const tableX = margin;
  const headerRowH = 22;
  const rowH = 25;
  const drawTableHeader = () => {
    ensureSpace(headerRowH + rowH);
    page.drawRectangle({ x: tableX, y: y - headerRowH, width: tableW, height: headerRowH, color: REPORT_GREEN });
    let x = tableX;
    columns.forEach((column) => {
      const headerWidth = pdfTextWidth(fontBold, column.label, 6.5);
      const textX = column.align === "right" ? x + column.width - headerWidth - 5 : column.align === "center" ? x + (column.width - headerWidth) / 2 : x + 5;
      text(page, column.label, textX, y - 14, { size: 6.5, bold: true, color: rgb(1, 1, 1) });
      if (x !== tableX) page.drawLine({ start: { x, y: y - headerRowH }, end: { x, y }, thickness: 0.35, color: rgb(1, 1, 1) });
      x += column.width;
    });
    y -= headerRowH;
  };

  drawTableHeader();
  leads.forEach((lead, index) => {
    if (y - rowH < contentBottom) {
      newContentPage();
      drawTableHeader();
    }
    if (index % 2) page.drawRectangle({ x: tableX, y: y - rowH, width: tableW, height: rowH, color: rgb(0.97, 0.98, 0.96) });
    page.drawRectangle({ x: tableX, y: y - rowH, width: tableW, height: rowH, borderColor: ODOO_BORDER, borderWidth: 0.35 });
    const values = [lead.name || "-", stageLabel(lead.stage_id), salesLabel(lead), money(lead.expected_revenue), `${Number(lead.probability) || 0}%`, dateLabel(lead.date_deadline)];
    let x = tableX;
    columns.forEach((column, columnIndex) => {
      const lines = generateWrappedLines(font, values[columnIndex], column.width - 10, 6.2, 2);
      lines.forEach((line, lineIndex) => {
        const lineWidth = pdfTextWidth(font, line, 6.2);
        const textX = column.align === "right" ? x + column.width - lineWidth - 5 : column.align === "center" ? x + (column.width - lineWidth) / 2 : x + 5;
        text(page, line, textX, y - 10 - lineIndex * 8, { size: 6.2, color: columnIndex === 3 ? REPORT_DARK : TEXT_DARK, bold: columnIndex === 3 });
      });
      if (x !== tableX) page.drawLine({ start: { x, y: y - rowH }, end: { x, y }, thickness: 0.25, color: ODOO_BORDER });
      x += column.width;
    });
    y -= rowH;
  });

  if (!leads.length) {
    text(page, "Aucun enregistrement pour les filtres sélectionnés.", tableX + 7, y - 15, { size: 8, color: ODOO_MUTED });
    y -= rowH;
  }
  ensureSpace(25);
  page.drawRectangle({ x: tableX, y: y - 21, width: tableW, height: 21, color: REPORT_GREEN });
  text(page, `TOTAL (${leads.length})`, tableX + 6, y - 14, { size: 7, bold: true, color: rgb(1, 1, 1) });
  const totalValue = money(expectedTotal);
  text(page, totalValue, tableX + tableW - pdfTextWidth(fontBold, totalValue, 7) - 6, y - 14, { size: 7, bold: true, color: rgb(1, 1, 1) });
  y -= 35;

  ensureSpace(42);
  text(page, "RÉPARTITION PAR ÉTAPE", margin, y, { size: 8, bold: true, color: REPORT_GREEN });
  page.drawLine({ start: { x: margin, y: y - 4 }, end: { x: pageW - margin, y: y - 4 }, thickness: 0.7, color: REPORT_GREEN });
  y -= 18;
  const byStage = new Map();
  leads.forEach((lead) => {
    const key = stageLabel(lead.stage_id);
    const current = byStage.get(key) || { count: 0, total: 0 };
    current.count += 1;
    current.total += Number(lead.expected_revenue) || 0;
    byStage.set(key, current);
  });
  [...byStage.entries()].forEach(([name, values], index) => {
    if (y - 17 < contentBottom) newContentPage();
    if (index % 2) page.drawRectangle({ x: margin, y: y - 17, width: 330, height: 17, color: rgb(0.97, 0.98, 0.96) });
    text(page, name, margin + 5, y - 11, { size: 6.5, maxWidth: 200 });
    text(page, String(values.count), margin + 225, y - 11, { size: 6.5 });
    const value = money(values.total);
    text(page, value, margin + 330 - pdfTextWidth(font, value, 6.5) - 5, y - 11, { size: 6.5 });
    y -= 17;
  });

  pages.forEach((currentPage, index) => {
    drawHeader(currentPage, index + 1);
    drawFooter(currentPage);
  });
  return new Blob([await doc.save()], { type: "application/pdf" });
}

async function generateInvoicePdf({ invoice = {} }) {
  const doc = await PDFDocument.create();
  const font = await doc.embedFont(StandardFonts.Helvetica);
  const fontBold = await doc.embedFont(StandardFonts.HelveticaBold);
  const logoBytes = await fetchReportLogo();
  const logo = logoBytes ? await doc.embedPng(logoBytes) : null;
  const pageW = 595.28;
  const pageH = 841.89;
  const margin = 64;
  const tableX = margin + 6;
  const tableW = pageW - tableX - margin;
  const tableCols = [155, 65, 130, 140];
  const taxRate = 0.1925;
  const lines = Array.isArray(invoice.lines) ? invoice.lines : [];
  const untaxed = lines.reduce((sum, line) => sum + (Number(line.subtotal) || Number(line.price || 0) * Number(line.qty || 0)), 0);
  const tax = Math.round(untaxed * taxRate * 100) / 100;
  const total = untaxed + tax;
  const money = (value) => sanitizePdfText((Number(value) || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
  const text = (page, value, x, y, options = {}) => page.drawText(sanitizePdfText(value), {
    x, y, size: options.size || 8, font: options.bold ? fontBold : font,
    color: options.color || TEXT_DARK, maxWidth: options.maxWidth, lineHeight: options.lineHeight || 10,
  });
  const wrap = (value, maxWidth, size = 8) => generateWrappedLines(font, value || "-", maxWidth, size, 4);
  const drawCellText = (page, value, x, y, width, options = {}) => {
    const size = options.size || 8;
    const values = wrap(value, width - 10, size);
    values.forEach((line, index) => {
      const lineWidth = pdfTextWidth(options.bold ? fontBold : font, line, size);
      const textX = options.align === "right" ? x + width - lineWidth - 5 : options.align === "center" ? x + (width - lineWidth) / 2 : x + 5;
      text(page, line, textX, y - index * (size + 2), { size, bold: options.bold, color: options.color });
    });
  };
  const drawHeader = (page) => {
    page.drawRectangle({ x: 0, y: pageH - 48, width: pageW, height: 48, color: rgb(1, 1, 1) });
    page.drawRectangle({ x: 114, y: pageH - 108, width: 286, height: 60, color: REPORT_DARK });
    page.drawRectangle({ x: 378, y: pageH - 108, width: pageW - 378, height: 60, color: REPORT_GREEN });
    page.drawSvgPath(`M 350 ${pageH - 108} L 378 ${pageH - 48} L 408 ${pageH - 48} L 380 ${pageH - 108} Z`, { color: rgb(1, 1, 1) });
    if (logo) page.drawImage(logo, { x: 38, y: pageH - 105, width: 58, height: 58 });
    text(page, "AFRICA SYSTEMS  |  DBA  |  LEWOO SARL", 118, pageH - 82, { size: 9, bold: true, color: rgb(1, 1, 1) });
    text(page, "Power of innovation", 118, pageH - 99, { size: 8, color: rgb(1, 1, 1) });
    text(page, "Bonapriso Douala, Cameroon", 398, pageH - 78, { size: 7, bold: true, color: rgb(1, 1, 1), maxWidth: 178 });
    text(page, "+237 6 93 83 02 06", 423, pageH - 91, { size: 7, color: rgb(1, 1, 1) });
    text(page, "info@africasystems.com", 415, pageH - 103, { size: 7, color: rgb(1, 1, 1) });
  };
  const drawFooter = (page) => {
    page.drawRectangle({ x: 0, y: 28, width: pageW, height: 30, color: REPORT_GREEN });
    page.drawRectangle({ x: 0, y: 0, width: pageW, height: 28, color: REPORT_DARK });
    text(page, "THANK YOU FOR TRUSTING US ...", margin, 39, { size: 8, bold: true, color: rgb(1, 1, 1) });
    text(page, "+237 6 93 83 02 06 | info@africasystems.com | www.africasystems.com | M102015145032Z", 112, 8, { size: 5.5, color: rgb(1, 1, 1), maxWidth: pageW - 124 });
  };

  const page = doc.addPage([pageW, pageH]);
  drawHeader(page);
  let y = pageH - 145;
  const customer = invoice.customer || "Customer";
  text(page, customer, pageW / 2 - pdfTextWidth(fontBold, customer, 16) / 2, y, { size: 16, bold: true });
  y -= 24;
  const meta = [["Quotation", invoice.number || ""], ["Quotation Date", invoice.invoiceDate || ""], ["Expiration", invoice.dueDate || ""], ["Salesperson", invoice.buyer || ""]];
  meta.forEach(([label, value]) => {
    text(page, `${label} :`, margin, y, { size: 8, bold: true });
    text(page, value, margin + 58, y, { size: 8, color: REPORT_GREEN });
    y -= 17;
  });
  y -= 4;

  const drawTableHeader = () => {
    page.drawRectangle({ x: tableX, y: y - 23, width: tableW, height: 23, color: REPORT_GREEN });
    let x = tableX;
    ["Description", "Quantity", "Unit Price (FCFA)", "Amount (FCFA)"].forEach((label, index) => {
      drawCellText(page, label, x, y - 15, tableCols[index], { size: 7, bold: true, align: index === 0 ? "left" : "center", color: rgb(1, 1, 1) });
      x += tableCols[index];
    });
    y -= 23;
  };
  drawTableHeader();
  lines.forEach((line, lineIndex) => {
    const description = line.label || line.product || "-";
    const descLines = wrap(description, tableCols[0] - 10, 8);
    const rowH = Math.max(24, descLines.length * 10 + 8);
    page.drawRectangle({ x: tableX, y: y - rowH, width: tableW, height: rowH, borderColor: REPORT_GREEN, borderWidth: 0.35, color: lineIndex % 2 ? rgb(0.99, 0.99, 0.99) : rgb(1, 1, 1) });
    let x = tableX;
    drawCellText(page, description, x, y - 10, tableCols[0], { size: 8, bold: true });
    x += tableCols[0];
    drawCellText(page, String(line.qty ?? 0), x, y - 10, tableCols[1], { size: 8, align: "center" });
    x += tableCols[1];
    drawCellText(page, money(line.price), x, y - 10, tableCols[2], { size: 8, align: "right" });
    x += tableCols[2];
    drawCellText(page, money(line.subtotal), x, y - 10, tableCols[3], { size: 8, align: "right" });
    y -= rowH;
  });
  if (!lines.length) {
    drawCellText(page, "No invoice lines", tableX, y - 10, tableW, { size: 8 });
    y -= 24;
  }

  const totalsX = tableX + tableCols[0] + tableCols[1] + tableCols[2];
  const drawTotal = (label, value, color = TEXT_DARK, fill = null) => {
    if (fill) page.drawRectangle({ x: totalsX, y: y - 20, width: tableCols[3], height: 20, color: fill });
    text(page, label, totalsX - 4 - pdfTextWidth(fontBold, label, 8), y - 13, { size: 8, bold: true, color });
    drawCellText(page, value, totalsX, y - 13, tableCols[3], { size: 8, align: "right", color });
    y -= 20;
  };
  drawTotal("Untaxed Amount", money(untaxed));
  drawTotal("Tax (19.25)", money(tax));
  drawTotal("AMOUNT DUE", money(total), rgb(1, 1, 1), REPORT_GREEN);
  y -= 28;

  page.drawRectangle({ x: tableX, y: y - 25, width: tableW, height: 25, color: REPORT_GREEN });
  text(page, "PLEASE MAKE A PAYMENT TO :", tableX + tableW / 2 - pdfTextWidth(fontBold, "PLEASE MAKE A PAYMENT TO :", 8) / 2, y - 16, { size: 8, bold: true, color: rgb(1, 1, 1) });
  y -= 25;
  const bankH = 82;
  page.drawRectangle({ x: tableX, y: y - bankH, width: tableW, height: bankH, borderColor: ODOO_BORDER, borderWidth: 0.5 });
  page.drawLine({ start: { x: tableX + tableW / 2, y: y - bankH }, end: { x: tableX + tableW / 2, y }, thickness: 0.5, color: ODOO_BORDER });
  ["Bank: BGFIBank NOM: LEWOO SARL", "Account Number: 40007300011", "Mobile Payment:"].forEach((value, index) => text(page, value, tableX + 5, y - 18 - index * 22, { size: 7.5 }));
  ["Account Holder: LEWOO SARL", "Code Banque: 10035", "Payment terms : Immediate Payment", "OM : 657 05 15 80", "Title: DICKSON ZEMELE TAKOUZIM"].forEach((value, index) => text(page, value, tableX + tableW / 2 + 5, y - 18 - index * 14, { size: 7.5 }));
  drawFooter(page);
  return new Blob([await doc.save()], { type: "application/pdf" });
}

function downloadPdfBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || `rapport-crm-${new Date().toISOString().slice(0, 10)}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export { generateCrmPdf, generateInvoicePdf, downloadPdfBlob };